import type { OuraDataResponse, OuraTokens } from '@circa/shared/protocol';
import { api } from '@/core/api';
import { openExternal } from '@/core/platform';
import { batch } from '@/core/repo';
import { getSession } from '@/core/session';
import { getSetting, setSetting } from '@/core/settings';
import { addDays, todayStr } from '@/core/time';
import { ouraWorkoutType } from '@/modules/activity/workouts';
import { upsert } from './upsert';

// Oura: sleep sessions (with stages, heart rate, HRV) and daily scores, steps and calories.
// The server relays the requests (Oura's API isn't reachable from a browser); the tokens and
// all the data stay in this account, encrypted.

export const OURA_KEY = 'conn.oura';

export interface OuraLink {
  /** OAuth sign-in */
  tokens?: OuraTokens;
  /** or a personal access token */
  token?: string;
  connectedAt: number;
  lastSync?: number;
  /** the last day fully fetched */
  through?: string;
  /** what a sync brings in; when it grows (workouts), the next sync goes back a year again */
  version?: number;
  error?: string;
}

const SYNC_VERSION = 2;

export const getOura = () => getSetting<OuraLink | null>(OURA_KEY, null);

export interface OuraConfig {
  enabled: boolean;
  /** whose Oura app signs you in: the one you pasted here, or the server's */
  source: 'yours' | 'server' | null;
  /** your app's Client ID (never the secret) */
  clientId: string | null;
  /** what to enter as the Redirect URI in your Oura app */
  redirectUri: string;
  authorizeUrl: string | null;
}

export async function ouraConfig(): Promise<OuraConfig> {
  return api('/api/oura/config');
}

/** Save your Oura app's Client ID and secret (the server keeps the secret encrypted). */
export async function saveOuraApp(clientId: string, clientSecret: string): Promise<void> {
  await api('/api/oura/app', {
    method: 'PUT',
    body: { clientId: clientId.trim(), clientSecret: clientSecret.trim() },
  });
}

export async function removeOuraApp(): Promise<void> {
  await api('/api/oura/app', { method: 'DELETE' });
}

/** Sign in with Oura in the browser; resolves once the server has the tokens for us. */
export async function connectOuraOAuth(): Promise<boolean> {
  const cfg = await ouraConfig();
  if (!cfg.enabled || !cfg.authorizeUrl)
    throw new Error(
      'Oura sign-in isn’t set up on your server. Paste a personal access token instead.',
    );
  const userId = getSession().account?.userId;
  if (!userId) throw new Error('Connect this device to your server first (Settings → Sync).');
  const rand = [...crypto.getRandomValues(new Uint8Array(18))]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const state = `${userId}-${rand}`;
  await openExternal(`${cfg.authorizeUrl}&state=${encodeURIComponent(state)}`);
  for (let i = 0; i < 150; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    const { tokens } = await api<{ tokens: OuraTokens | null }>(
      `/api/oura/claim?state=${encodeURIComponent(state)}`,
    );
    if (tokens) {
      setSetting(OURA_KEY, { tokens, connectedAt: Date.now() } satisfies OuraLink);
      return true;
    }
  }
  return false;
}

export function connectOuraToken(token: string) {
  setSetting(OURA_KEY, { token: token.trim(), connectedAt: Date.now() } satisfies OuraLink);
}

export function disconnectOura() {
  setSetting(OURA_KEY, null);
}

/** "+03:00" at the end of an ISO time → 180. */
export function isoOffset(iso: string): number | undefined {
  const m = /(Z|([+-])(\d{2}):?(\d{2}))$/.exec(iso.trim());
  if (!m) return undefined;
  if (m[1] === 'Z') return 0;
  return (m[2] === '-' ? -1 : 1) * (Number(m[3]) * 60 + Number(m[4]));
}

export function applyOura(data: OuraDataResponse): number {
  let n = 0;
  batch(() => {
    for (const s of data.sleep) {
      const r = upsert('sleep', `oura-${s.id}`, {
        day: s.day,
        type: s.type,
        start: Date.parse(s.start),
        end: Date.parse(s.end),
        startOffset: isoOffset(s.start),
        endOffset: isoOffset(s.end),
        totalS: s.totalS,
        inBedS: s.inBedS,
        awakeS: s.awakeS,
        remS: s.remS,
        deepS: s.deepS,
        lightS: s.lightS,
        latencyS: s.latencyS,
        efficiency: s.efficiency,
        hrAvg: s.hrAvg,
        hrLow: s.hrLow,
        hrv: s.hrv,
        breath: s.breath,
        phases: s.phases,
        source: 'oura',
      });
      if (r !== 'same') n++;
    }
    for (const d of data.days) {
      const r = upsert('day', `day-${d.day}`, {
        day: d.day,
        sleepScore: d.sleepScore,
        readiness: d.readiness,
        tempDev: d.tempDev,
        steps: d.steps,
        activeKcal: d.activeKcal,
        totalKcal: d.totalKcal,
        activityScore: d.activityScore,
        walkM: d.walkM,
      });
      if (r !== 'same') n++;
    }
    for (const w of data.workouts ?? []) {
      const start = Date.parse(w.start);
      const r = upsert(
        'workout',
        `oura-w-${w.id}`,
        {
          start,
          durationS: Math.max(0, Math.round((Date.parse(w.end) - start) / 1000)),
          distanceM: w.distanceM,
          type: w.label || ouraWorkoutType(w.activity),
          kcal: w.kcal,
          intensity: w.intensity,
          source: 'oura',
        },
        // a type you changed here stays yours
        ['type'],
      );
      if (r !== 'same') n++;
    }
  });
  return n;
}

const CHUNK_DAYS = 60;

/** Fetch what's new (the last few days again, Oura revises them). First time: a year back. */
export async function syncOura(): Promise<number> {
  const link = getOura();
  if (!link || (!link.tokens && !link.token)) return 0;
  const today = todayStr();
  let from =
    link.through && (link.version ?? 1) >= SYNC_VERSION
      ? addDays(link.through, -3)
      : addDays(today, -365);
  let changed = 0;
  let tokens = link.tokens;
  try {
    while (from <= today) {
      const to = addDays(from, CHUNK_DAYS - 1) < today ? addDays(from, CHUNK_DAYS - 1) : today;
      const data = await api<OuraDataResponse>('/api/oura/data', {
        method: 'POST',
        body: { ...(tokens ? { tokens } : { token: link.token }), start: from, end: to },
      });
      if (data.tokens) tokens = data.tokens;
      changed += applyOura(data);
      from = addDays(to, 1);
    }
    setSetting(OURA_KEY, {
      ...getOura(),
      ...link,
      tokens,
      lastSync: Date.now(),
      through: today,
      version: SYNC_VERSION,
      error: undefined,
    });
  } catch (err) {
    setSetting(OURA_KEY, { ...link, tokens, error: (err as Error).message });
    throw err;
  }
  return changed;
}
